import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdminControls } from "../../state/AdminControlsContext";
import { useAdminService } from "../../data/adminService";
import { useAsyncData } from "../../hooks/useAsyncData";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatNumberDe } from "../../utils/format";
import { toggleSortDirection } from "../../utils/sort";
import type { AdminSubStatus, AdminUserSort, SortDirection } from "../../graphql/types";
import { UserFilterBar } from "./components/UserFilterBar";
import { UsersTable } from "./components/UsersTable";
import "./users.css";

const PAGE_SIZE = 50;

/** Default sort direction when switching to a new key (name is alpha-ascending). */
function defaultDir(key: AdminUserSort): SortDirection {
  return key === "NAME" ? "ASC" : "DESC";
}

/**
 * Users screen (FE-5): server-side search/filter/sort/paginate over all users.
 * Search comes from the shared Topbar (debounced); sub-filter, trial-only, sort
 * and paging are local. Rows are display-only (User Detail is Post-V1).
 * Mock-first until BE-4 lands (flip REACT_APP_USE_MOCK).
 */
export const UsersScreen: React.FC = () => {
  const { t } = useTranslation();
  const { search, setSearch } = useAdminControls();
  const service = useAdminService();

  const debouncedSearch = useDebouncedValue(search, 250);
  const [status, setStatus] = useState<AdminSubStatus>("ALL");
  const [sort, setSort] = useState<AdminUserSort>("LOGIN");
  const [sortDir, setSortDir] = useState<SortDirection>("DESC");
  const [take, setTake] = useState(PAGE_SIZE);

  // Any filter/sort/search change resets paging back to the first page.
  useEffect(() => {
    setTake(PAGE_SIZE);
  }, [debouncedSearch, status, sort, sortDir]);

  const { data, loading, error, reload } = useAsyncData(
    () =>
      service.getUsers({
        search: debouncedSearch.trim() || undefined,
        status,
        sort,
        sortDir,
        skip: 0,
        take,
      }),
    [service, debouncedSearch, status, sort, sortDir, take],
  );

  const handleSort = (key: AdminUserSort) => {
    if (key === sort) {
      setSortDir(toggleSortDirection);
    } else {
      setSort(key);
      setSortDir(defaultDir(key));
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("ALL");
  };

  const filteredCount = data?.filteredCount ?? 0;
  const totalCount = data?.totalCount ?? 0;
  const hasMore = !!data && data.items.length < filteredCount;

  return (
    <div className="shell-content-inner">
      <div className="view-header users-header">
        <div>
          <h1 className="view-title">{t("users.title")}</h1>
          <p className="view-subtitle">
            <span className="users-count">{formatNumberDe(filteredCount)}</span>{" "}
            {t("users.subtitleSuffix", { total: formatNumberDe(totalCount) })}
          </p>
        </div>
        <button className="users-refresh" onClick={reload} disabled={loading}>
          <RefreshIcon />
          {t("users.refresh")}
        </button>
      </div>

      <UserFilterBar
        status={status}
        sort={sort}
        onStatus={setStatus}
      />

      {error ? (
        <div className="lyra-error">
          <span>{t("users.error")}</span>
          <button className="range-btn range-btn--ghost" onClick={reload}>
            {t("dashboard.retry")}
          </button>
        </div>
      ) : (
        <>
          <UsersTable
            items={data?.items ?? null}
            loading={loading}
            sort={sort}
            sortDir={sortDir}
            onSort={handleSort}
            onResetFilters={resetFilters}
          />
          {hasMore && (
            <div className="users-loadmore">
              <button
                className="range-btn range-btn--ghost"
                onClick={() => setTake((n) => n + PAGE_SIZE)}
                disabled={loading}
              >
                {t("users.loadMore")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RefreshIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-2.6-6.4" />
    <path d="M21 4v5h-5" />
  </svg>
);
